import React from "react";
import styled from "styled-components";
import Links from "../../../Links";
import Image from "../../../Image";
import Rule from "../../../Rule";
import Heading from "../../../Heading";
import CaseStudyProfile from "../CaseStudiesProfile";
import CaseStudyHeading from "../CaseStudiesHeading";
import TechnologyUsedDesignTokens from "../../../../img/casestudy/technology-used/technology-used-design-tokens.jpg";
import TechnologyUsedStack from "../../../../img/casestudy/technology-used/technology-used-stack.png";
import CaseStudiesCard from "../CaseStudiesCard";
import Box from "../../../Box";
import Stack from "../../../Stack";
import BlogLayout from "../BlogLayout";

const ImageWrapper = styled.div`
  margin-bottom: 24px;
`;

const CodeElementBlock = styled.section`
  display: block;
  width: 100%;
  padding: var(--spacing-small);
  background: #00000069;
  border-radius: var(--radius-card);
  margin-bottom: 0;

  border: 1px solid #ffffff1f;
`;

const CodeElement = styled.code`
  margin-bottom: var(--spacing-small);
  display: block;
  &:last-child {
    margin-bottom: var(--spacing-clear);
  }
  &:empty {
    display: none;
  }
`;

const Technology = () => {
  return (
    <React.Fragment>
      <CaseStudyHeading title="Technology" />
      <CaseStudyProfile>
        <p>
          My portfolio website has been through many exciting iterations, as this is the playground for personal growth
          within the Front-End environment.
        </p>
        <p>
          When I decided to iterate on my website, I looked to use my personal reset and base CSS, MUI{" "}
          <Links href="https://muibook.com" target="_blank">
            native web component project
          </Links>{" "}
          to provide base elements to build the experience, React CLI for bringing life to the website, CSS Variables to
          define Design Tokens and Styled-Components to introduce specific page layout and application component styles.
        </p>
      </CaseStudyProfile>
      <CaseStudiesCard>
        <Heading
          variant="smaller"
          style={{ marginTop: 0, marginBottom: "var(--spacing-x-tiny)", textTransform: "uppercase" }}
        >
          Article
        </Heading>
        <p style={{ marginBottom: 0, fontSize: "var(--font-size-text-medium)" }}>
          A look at scalable front-end foundations and design token strategies.
        </p>
        <Rule spacing="medium" inverted mt="var(--spacing-medium)" mb="var(--spacing-large)" />

        <BlogLayout>
          <Stack spacing="var(--spacing-xx-large)">
            <Box>
              <Heading variant="larger">Front-end Tooling</Heading>
              <p>
                Over the years of creating websites, I have meticulously crafted a CSS file overtime that has many
                design decisions embedded within it. I have used it at the very heart of this website since its
                inception, and I continue to use it today.
              </p>
              <p>
                The way people think about styling websites and applications has changed, and my latest addiction is to
                understand how that consolidated list of styles can be broken down into a lightweight base for any
                modern day website.
              </p>
              <p>
                When simplifying the Mui Reset and Base styles, I also had reusable chunks of CSS that would help speed
                up the process of building familiar UI, and we call these components. In the next section, I will talk
                about how I extracted these styles and arranged them in a way that can be ported to any modern-day
                Front-End stack, such as Native Web Components, Vue or React.
              </p>
            </Box>

            <Box>
              <Heading variant="large">Mui Web Components</Heading>

              <p>
                Earlier, I ran into an issue using web components within React, where referencing the CSS reset inside
                the shadow DOM didn’t work. I suspect it was related to how React compiles CSS and handles paths.
              </p>
              <CodeElementBlock>
                <CodeElement>@import url("css/mui-reset.css");</CodeElement>
              </CodeElementBlock>
              <p style={{ marginTop: "var(--spacing-medium)" }}>
                Initially, I considered either refactoring the native web component project to include more reset styles
                inside each component or porting them to React. Whatever choice I made would shape the direction of the
                personal project.
              </p>
              <p>
                Now, with{" "}
                <Links href="https://muibook.com" target="_blank">
                  muibook.com
                </Links>{" "}
                , I’ve overcome that challenge. The Design System is built using Web Components with resets contained
                within the components and tied to CSS Variables for consistency and scalability.{" "}
                <Links href="#muibook">Learn More</Links>.
              </p>
              <p>
                Going forward, I plan to refit my website using the Muibook Design System, ensuring the visual aesthetic
                is maintained while leveraging the system’s flexibility.
              </p>
            </Box>
            <Box>
              <Heading variant="large">React CLI, Github and Netlify</Heading>
              <p>
                I use a combination of tools to create and push updates to my website. The process is largely automated
                thanks to GitHub and Netlify. Whenever I make changes, whether tweaking a component, updating content,
                or adjusting styles, the workflow is simple and efficient:
              </p>
              <ol style={{ marginBottom: 0 }}>
                <li>Commit that change to my personal Github account</li>
                <li style={{ marginBottom: 0 }}>
                  Netlify will then deploy the changes to{" "}
                  <Links href="https://michaeltrilford.co.nz/" target="_blank">
                    michaeltrilford.co.nz
                  </Links>
                </li>
              </ol>
            </Box>
            <Box>
              <Heading variant="large">CSS Variables</Heading>
              <p>
                Since I'm only supporting modern browsers on my website, I have opted to use CSS Variables as my Design
                Token Library. The naming convention adopted here is different from the Feelix approach, as I wasn't
                limited on the JSON structure.
              </p>
              <Image
                dark
                src={TechnologyUsedDesignTokens}
                alt="Agnostic design tokens"
                caption="Agnostic design tokens"
                spacing="clear"
              />
              <p style={{ marginTop: "var(--spacing-medium)" }}>
                The tokens consist of a foundational set, which reference colours, font sizes, etc. Then these
                foundation tokens are mapped to specific uses cases, such as the colour of a link.
              </p>
              <p>
                When I update the foundational components to use Mui Components , I will be shifting to the design token
                structure outlined{" "}
                <Links href="https://muibook.com/#/base-theme" target="_blank">
                  muibook.com
                </Links>
                .
              </p>
            </Box>
            <Box>
              <Heading variant="large">Styled-Components</Heading>
              <p>
                Working with Styled-Components is terrific, as I find it helps to improve the overall architecture of
                the codebase.
              </p>
              <p>
                If you are familiar with Sass, then you will feel right at home, as you have access to nesting and other
                useful techniques. The bonus with Styled-Components is that any style introduced is encapsulated within
                that specific file.
              </p>
              <p>
                You can continue to use classNames to style your custom elements or slight modifications to existing
                components.
              </p>
              <p>
                Alternatively, you can use Styled-Components to declare a component that has specific styles associated
                with it, that you can reuse within that document.
              </p>
              <CodeElementBlock>
                <CodeElement>const ContainerElement = styled.div</CodeElement>
              </CodeElementBlock>
              <p style={{ marginTop: "var(--spacing-medium)" }}>
                The great thing about this, it helps identify new components that you could lift out of the specific
                file, and add globally to your website or application so that you can use it elsewhere.
              </p>
            </Box>

            <Stack spacing="var(--spacing-large)">
              <Box>
                <Heading variant="large">Styled-System</Heading>
                <p>
                  <Links
                    href="https://github.com/styled-system/styled-system"
                    rel="noopener noreferrer"
                    target="_blank"
                  >
                    Styled System
                  </Links>{" "}
                  provides style API's to give users a way to attach additional styles to React components when wanting
                  to craft spacing, layout, the typography of an experience.
                </p>
              </Box>

              <Stack spacing="var(--spacing-large)">
                <Box>
                  <Heading variant="small">Utilising the theme</Heading>
                  <p>
                    With the CSS Variables (design tokens) I mentioned earlier, I have mapped them to the
                    styled-components `Theme.js` file. I have opted for a more refined naming convention, which I will
                    introduce to my CSS Variables in future. The good thing is, they're agnostic, so I can map them
                    together, and iterate on that naming convention at another time.
                  </p>
                  <CodeElementBlock>
                    <CodeElement>
                      &#47;&#47; Theme.js snippet
                      <br />
                      <br />
                      space: &#123;
                      <br />
                      &nbsp;&nbsp;largest: "var(--spacing-x-large)",
                      <br />
                      &nbsp;&nbsp;larger: "var(--spacing-large)",
                      <br />
                      &nbsp;&nbsp;large: "var(--spacing-x-medium)",
                      <br />
                      &nbsp;&nbsp;medium: "var(--spacing-medium)",
                      <br />
                      &nbsp;&nbsp;small: "var(--spacing-small)",
                      <br />
                      &nbsp;&nbsp;smaller: "var(--spacing-x-small)",
                      <br />
                      &nbsp;&nbsp;smallest: "var(--spacing-tiny)",
                      <br />
                      &nbsp;&nbsp;clear: "var(--spacing-clear)",
                      <br />
                      &#125;
                    </CodeElement>
                  </CodeElementBlock>
                </Box>

                <Box>
                  <Heading variant="small" mb="smaller" mt="0">
                    Simple styled-system components
                  </Heading>
                  <p>
                    {" "}
                    You can create powerful layout components by simple combining Styled-components and Styled-system
                    together. The seemingly inline style will transpile into the application head element, so the CSS is
                    performant.
                  </p>
                  <CodeElementBlock>
                    <CodeElement>
                      const Box = styled.div`
                      <br />
                      &nbsp;&nbsp;${`{space}`}
                      <br />
                      &nbsp;&nbsp;${`{layout}`}
                      <br />
                      &nbsp;&nbsp;${`{color}`}
                      <br />
                      &nbsp;&nbsp;${`{flexbox}`}
                      <br />
                      `;
                    </CodeElement>
                    <CodeElement>&#60;Box marginBottom="small" /&#62; `;</CodeElement>
                  </CodeElementBlock>
                </Box>

                <Box>
                  <Heading variant="small" mb="smaller" mt="0">
                    Complex styled-system components
                  </Heading>
                  <p>
                    Remember to spread the props when installing styled-components inside your React components.
                    Additionally, add the styled-system API's at the end of the styled-components styles, as this will
                    allow the custom styles to override the foundation styles naturally.
                  </p>
                  <CodeElementBlock>
                    <CodeElement>
                      const LinkElement = styled.a`
                      <br />
                      &nbsp;&nbsp;... relevant styles ...
                      <br />
                      &nbsp;&nbsp;${`{space}`}
                      <br />
                      &nbsp;&nbsp;${`{layout}`}
                      <br />
                      &nbsp;&nbsp;${`{color}`}
                      <br />
                      &nbsp;&nbsp;${`{flexbox}`}
                      <br />
                      `;
                    </CodeElement>
                    <CodeElement>
                      const Link = &#40;&#123;
                      <br />
                      &nbsp;&nbsp;children,
                      <br />
                      &nbsp;&nbsp;...props
                      <br />
                      &#125;&#41; =&#62; &#123;
                      <br />
                      &nbsp;&nbsp;return &#40;
                      <br />
                      &nbsp;&nbsp;&nbsp;&nbsp;&#60;LinkElement&nbsp;&#123;...props&#125;&#62;
                      <br />
                      &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&#123;children&#125;
                      <br />
                      &nbsp;&nbsp;&nbsp;&nbsp;&#60;/LinkElement&#62;
                      <br />
                      &nbsp;&nbsp;&#41;;
                      <br />
                      &#125;;
                    </CodeElement>
                  </CodeElementBlock>
                </Box>
              </Stack>
            </Stack>

            <Stack spacing="var(--spacing-large)">
              <Heading style={{ marginBottom: 0 }} variant="large">
                Evolving My Styling Approach (2025)
              </Heading>

              <Stack spacing="var(--spacing-large)">
                <Box>
                  <Heading variant="small">From Rigid Styles to Composable Layouts</Heading>
                  <p>
                    Over time, my approach to foundational styling has shifted toward simplicity and composability. I
                    have moved away from applying rigid base styles, such as default margin-bottom on elements, in
                    favour of context-aware layout patterns using components like Stack or Box. This provides greater
                    flexibility, particularly when creating custom layouts. For example, while a marketing website might
                    still benefit from consistent vertical rhythm, web apps often require more precise control.
                  </p>
                </Box>
                <Box>
                  <Heading variant="small">Evolving Beyond CSS-in-JS</Heading>
                  <p>
                    While I previously used Styled-components and later Emotion.js for component styling, I have now
                    moved away from CSS-in-JS for component creation in my personal Web Component Design System. With
                    Muibook.com, I’ve built a system where resets are contained within each component, CSS Variables
                    ensure consistency, and multi-brand theming and strategy are built in. This approach keeps CSS
                    neatly packaged while providing flexibility and scalability.
                  </p>
                </Box>
                <ImageWrapper>
                  <Image
                    dark
                    src={TechnologyUsedStack}
                    alt="Modernising My Foundations"
                    caption="Modernising My Foundations"
                  />
                </ImageWrapper>
                <Box>
                  <Heading variant="small">Keeping Flexibility for Applications</Heading>
                  <p>
                    That said, I still use Emotion and CSS Modules for general application building and composing
                    components, and I’m proud to have built a component library that is both flexible and reusable
                    across different projects.
                  </p>
                </Box>
                <Box>
                  <Heading variant="small">Looking Ahead: Muibook in Action</Heading>
                  <p style={{ marginBottom: 0 }}>
                    Going forward, I plan to refit my website using the Muibook Design System, maintaining the visual
                    aesthetic while leveraging the system’s flexibility. Learn more about the project at{" "}
                    <Links href="https://muibook.com" target="_blank">
                      muibook.com
                    </Links>
                    .
                  </p>
                </Box>
              </Stack>
            </Stack>
          </Stack>
        </BlogLayout>
      </CaseStudiesCard>
    </React.Fragment>
  );
};

export default Technology;
